import React from "react";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  OfficeSelectorInputProps,
  OfficeSelectorInputPropsWithFormControl
} from "./OfficeSelectorInput.types";
import { SelectOffices } from "./SelectOffices";

const OfficeSelectorInputInput = (
  props: OfficeSelectorInputProps & { label?: string }
) => {
  const {
    onChange,
    offices = [],
    defaultValue,
    disabled,
    showSelectedLabel = true,
    allowClear = true,
    triggerClassName,
    multiselect = false,
    splitView = false,
    withApply = false,
    trigger,
    placeholder = "Select an office",
    contentWidth,
    variant = "default",
    inputClassName,
    unselectableOfficeIds,
    unselectableOfficeReason,
    selectableOfficeIds,
    label,
    ...rest
  } = props;

  return (
    <SelectOffices
      onChange={onChange || (() => {})}
      offices={offices}
      defaultValue={defaultValue}
      disabled={disabled}
      showSelectedLabel={showSelectedLabel}
      allowClear={allowClear}
      triggerClassName={triggerClassName || inputClassName}
      multiselect={multiselect}
      splitView={splitView}
      withApply={withApply}
      trigger={trigger}
      placeholder={placeholder}
      contentWidth={contentWidth}
      variant={variant}
      unselectableOfficeIds={unselectableOfficeIds}
      unselectableOfficeReason={unselectableOfficeReason}
      selectableOfficeIds={selectableOfficeIds}
      label={label}
    />
  );
};

/**
 * An office field: picks one office, or several with `multiselect`, from the organization's
 * divisions (loaded with `useDivisionsMenuQuery`), browsed by division or searched by name.
 * `offices` / `onChange` carry arrays of office ids, also in single select. Renders nothing
 * until the divisions have loaded. For toolbars without a field around it use `SelectOffices`.
 * Inside react-hook-form use `HookFormOfficeSelectorInput`.
 */
export const OfficeSelectorInput = React.forwardRef<
  React.ComponentRef<typeof FormControlWrapper>,
  OfficeSelectorInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);
  const { disableOutline, ...restInputProps } = inputProps;

  return (
    <FormControlWrapper
      variant={"unstyled"}
      wrapperClassName={"py-0"}
      enableSlotsInputFocus
      ref={ref}
      {...wrapperProps}
    >
      {/* The trigger is not a labelable input, so it announces the label itself */}
      <OfficeSelectorInputInput
        label={
          typeof wrapperProps.label === "string"
            ? wrapperProps.label
            : undefined
        }
        {...restInputProps}
      />
      {disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
}) as (
  props: OfficeSelectorInputPropsWithFormControl & {
    ref?: React.Ref<React.ComponentRef<typeof FormControlWrapper>>;
  }
) => React.ReactElement;
